import * as THREE from 'three';
import { Utils } from '../../shared/utils.js';
import { Gradients, Gradient } from '../../viewer-utils/materials/gradients.js';
import { Shaders } from './shaders/shaders.js';
import { ClassificationScheme, ClassificationEntry } from './classification-scheme.js';
import { PolygonClipVolume } from '../../viewer-utils/tools/polygon-clip-volume.js';
import {
  PointSizeType,
  PointShape,
  TreeType,
  ElevationGradientRepeat,
} from '../../shared/defines.js';

export interface PotreeUniform extends THREE.IUniform {
  type: string;
}

export interface ClipBoxDescriptor {
  box: THREE.Object3D;
  inverse: THREE.Matrix4;
  position: THREE.Vector3;
}

interface PointCloudMaterialParameters {
  size?: number;
  minSize?: number;
  maxSize?: number;
  treeType?: TreeType;
}

type EventListenerMap = Record<string, Array<(event: THREE.Event) => void>>;

function getValid<T>(value: T | undefined, fallback: T): T {
  return value !== undefined ? value : fallback;
}

export class PointCloudMaterial extends THREE.RawShaderMaterial {
  visibleNodesTexture: THREE.DataTexture;
  classificationTexture: THREE.DataTexture;
  gradientTexture: THREE.Texture;
  classification: Record<number | 'DEFAULT', ClassificationEntry>;
  ranges: Map<string, [number, number] | null>;
  clipBoxes: ClipBoxDescriptor[];
  clipPolygons: PolygonClipVolume[];
  attributes: Record<string, { type: string; value: unknown[] }>;
  uniforms!: Record<string, PotreeUniform>;

  private _pointSizeType: number;
  private _shape: number;
  private _useClipBox: boolean;
  private _weighted: boolean;
  private _gradient: Gradient;
  private _treeType: number;
  private _useEDL: boolean;
  private _activeAttributeName: string | null;
  private _defines = new Map<string, string>();
  _defaultIntensityRangeChanged: boolean;
  _defaultElevationRangeChanged: boolean;
  _defaultExtraRangeChanged?: boolean;
  private _hiddenListeners?: EventListenerMap;

  constructor(parameters: PointCloudMaterialParameters = {}) {
    super();

    this.visibleNodesTexture = Utils.generateDataTexture(2048, 1, new THREE.Color(0xffffff));
    this.visibleNodesTexture.minFilter = THREE.NearestFilter;
    this.visibleNodesTexture.magFilter = THREE.NearestFilter;

    const pointSize = getValid(parameters.size, 1.0);
    const minSize = getValid(parameters.minSize, 2.0);
    const maxSize = getValid(parameters.maxSize, 50.0);
    const treeType = getValid(parameters.treeType, TreeType.OCTREE);

    this._pointSizeType = PointSizeType.FIXED;
    this._shape = PointShape.SQUARE;
    this._useClipBox = false;
    this.clipBoxes = [];
    this.clipPolygons = [];
    this._weighted = false;
    this._gradient = Gradients['SPECTRAL'];
    this.gradientTexture = PointCloudMaterial.generateGradientTexture(this._gradient);
    this.lights = false;
    this.fog = false;
    this._treeType = treeType;
    this._useEDL = false;

    this.ranges = new Map();

    this._activeAttributeName = null;

    this._defaultIntensityRangeChanged = false;
    this._defaultElevationRangeChanged = false;

    {
      const [width, height] = [256, 1];
      const data = new Uint8Array(width * 4);
      const texture = new THREE.DataTexture(data, width, height, THREE.RGBAFormat);
      texture.magFilter = THREE.NearestFilter;
      texture.needsUpdate = true;

      this.classificationTexture = texture;
    }

    this.attributes = {
      position: { type: 'fv', value: [] },
      color: { type: 'fv', value: [] },
      normal: { type: 'fv', value: [] },
      intensity: { type: 'f', value: [] },
      classification: { type: 'f', value: [] },
      returnNumber: { type: 'f', value: [] },
      numberOfReturns: { type: 'f', value: [] },
      pointSourceID: { type: 'f', value: [] },
      indices: { type: 'fv', value: [] },
    };

    this.uniforms = {
      level: { type: 'f', value: 0.0 },
      vnStart: { type: 'f', value: 0.0 },
      spacing: { type: 'f', value: 1.0 },
      blendHardness: { type: 'f', value: 2.0 },
      blendDepthSupplement: { type: 'f', value: 0.0 },
      fov: { type: 'f', value: 1.0 },
      screenWidth: { type: 'f', value: 1.0 },
      screenHeight: { type: 'f', value: 1.0 },
      near: { type: 'f', value: 0.1 },
      far: { type: 'f', value: 1.0 },
      uColor: { type: 'c', value: new THREE.Color(0xffffff) },
      uOpacity: { type: 'f', value: 1.0 },
      size: { type: 'f', value: pointSize },
      minSize: { type: 'f', value: minSize },
      maxSize: { type: 'f', value: maxSize },
      octreeSize: { type: 'f', value: 0 },
      bbSize: { type: 'fv', value: [0, 0, 0] },
      elevationRange: { type: '2fv', value: [0, 0] },

      clipBoxCount: { type: 'f', value: 0 },
      clipPolygonCount: { type: 'i', value: 0 },
      clipBoxes: { type: 'Matrix4fv', value: [] },
      clipPolygons: { type: '3fv', value: [] },
      clipPolygonVCount: { type: 'iv', value: [] },
      clipPolygonVP: { type: 'Matrix4fv', value: [] },

      visibleNodes: { type: 't', value: this.visibleNodesTexture },
      pcIndex: { type: 'f', value: 0 },
      gradient: { type: 't', value: this.gradientTexture },
      classificationLUT: { type: 't', value: this.classificationTexture },
      uHQDepthMap: { type: 't', value: null },
      toModel: { type: 'Matrix4f', value: [] },
      diffuse: { type: 'fv', value: [1, 1, 1] },
      transition: { type: 'f', value: 0.5 },

      intensityRange: { type: 'fv', value: [Infinity, -Infinity] },

      intensity_gbc: { type: 'fv', value: [1, 0, 0] },
      uRGB_gbc: { type: 'fv', value: [1, 0, 0] },
      wRGB: { type: 'f', value: 1 },
      wIntensity: { type: 'f', value: 0 },
      wElevation: { type: 'f', value: 0 },
      wClassification: { type: 'f', value: 0 },
      wReturnNumber: { type: 'f', value: 0 },
      wSourceID: { type: 'f', value: 0 },
      useOrthographicCamera: { type: 'b', value: false },
      elevationGradientRepat: { type: 'i', value: ElevationGradientRepeat.CLAMP },
      clipTask: { type: 'i', value: 1 },
      clipMethod: { type: 'i', value: 1 },
      uShadowColor: { type: '3fv', value: [0, 0, 0] },

      uExtraScale: { type: 'f', value: 1 },
      uExtraOffset: { type: 'f', value: 0 },
      uExtraRange: { type: '2fv', value: [0, 1] },
      uExtraGammaBrightContr: { type: '3fv', value: [1, 0, 0] },

      uFilterReturnNumberRange: { type: 'fv', value: [0, 7] },
      uFilterNumberOfReturnsRange: { type: 'fv', value: [0, 7] },
      uFilterGPSTimeClipRange: { type: 'fv', value: [0, 7] },
      uFilterPointSourceIDClipRange: { type: 'fv', value: [0, 65535] },
      backfaceCulling: { type: 'b', value: false },
    } as unknown as Record<string, PotreeUniform>;

    this.classification = ClassificationScheme.DEFAULT;

    (this.defaultAttributeValues as Record<string, number[]>)['normal'] = [0, 0, 0];
    (this.defaultAttributeValues as Record<string, number[]>)['classification'] = [0, 0, 0];
    (this.defaultAttributeValues as Record<string, number[]>)['indices'] = [0, 0, 0, 0];

    this.vertexShader = Shaders['pointcloud.vs'];
    this.fragmentShader = Shaders['pointcloud.fs'];

    this.vertexColors = true;

    this.updateShaderSource();
  }

  setDefine(key: string, value: string | undefined | null): void {
    if (value !== undefined && value !== null) {
      if (this._defines.get(key) !== value) {
        this._defines.set(key, value);
        this.updateShaderSource();
      }
    } else {
      this.removeDefine(key);
    }
  }

  removeDefine(key: string): void {
    this._defines.delete(key);
  }

  updateShaderSource(): void {
    let vs = Shaders['pointcloud.vs'];
    let fs = Shaders['pointcloud.fs'];
    const definesString = this.getDefines();

    const vsVersionIndex = vs.indexOf('#version ');
    const fsVersionIndex = fs.indexOf('#version ');

    if (vsVersionIndex >= 0) {
      vs = vs.replace(/(#version .*)/, `$1\n${definesString}`);
    } else {
      vs = `${definesString}\n${vs}`;
    }

    if (fsVersionIndex >= 0) {
      fs = fs.replace(/(#version .*)/, `$1\n${definesString}`);
    } else {
      fs = `${definesString}\n${fs}`;
    }

    this.vertexShader = vs;
    this.fragmentShader = fs;

    if (this.opacity === 1.0) {
      this.blending = THREE.NoBlending;
      this.transparent = false;
      this.depthTest = true;
      this.depthWrite = true;
      this.depthFunc = THREE.LessEqualDepth;
    } else if (this.opacity < 1.0 && !this._useEDL) {
      this.blending = THREE.AdditiveBlending;
      this.transparent = true;
      this.depthTest = false;
      this.depthWrite = true;
      this.depthFunc = THREE.AlwaysDepth;
    }

    if (this._weighted) {
      this.blending = THREE.AdditiveBlending;
      this.transparent = true;
      this.depthTest = true;
      this.depthWrite = false;
    }

    this.needsUpdate = true;
  }

  getDefines(): string {
    const defines: string[] = [];

    if (this._pointSizeType === PointSizeType.FIXED) {
      defines.push('#define fixed_point_size');
    } else if (this._pointSizeType === PointSizeType.ATTENUATED) {
      defines.push('#define attenuated_point_size');
    } else if (this._pointSizeType === PointSizeType.ADAPTIVE) {
      defines.push('#define adaptive_point_size');
    }

    if (this._shape === PointShape.SQUARE) {
      defines.push('#define square_point_shape');
    } else if (this._shape === PointShape.CIRCLE) {
      defines.push('#define circle_point_shape');
    } else if (this._shape === PointShape.PARABOLOID) {
      defines.push('#define paraboloid_point_shape');
    }

    if (this._useEDL) {
      defines.push('#define use_edl');
    }

    if (this._activeAttributeName) {
      const attributeName = this._activeAttributeName.replace(/[^a-zA-Z0-9]/g, '_');
      defines.push(`#define color_type_${attributeName}`);
    }

    if (this._treeType === TreeType.OCTREE) {
      defines.push('#define tree_type_octree');
    } else if (this._treeType === TreeType.KDTREE) {
      defines.push('#define tree_type_kdtree');
    }

    if (this._weighted) {
      defines.push('#define weighted_splats');
    }

    for (const value of this._defines.values()) {
      defines.push(value);
    }

    return defines.join('\n');
  }

  setClipBoxes(clipBoxes: ClipBoxDescriptor[]): void {
    if (!clipBoxes) {
      return;
    }

    const doUpdate =
      this.clipBoxes.length !== clipBoxes.length &&
      (clipBoxes.length === 0 || this.clipBoxes.length === 0);

    this.uniforms['clipBoxCount'].value = this.clipBoxes.length;
    this.clipBoxes = clipBoxes;

    if (doUpdate) {
      this.updateShaderSource();
    }

    this.uniforms['clipBoxes'].value = new Float32Array(this.clipBoxes.length * 16);

    for (let i = 0; i < this.clipBoxes.length; i++) {
      const box = clipBoxes[i];
      this.uniforms['clipBoxes'].value.set(box.inverse.elements, 16 * i);
    }

    for (let i = 0; i < this.uniforms['clipBoxes'].value.length; i++) {
      if (Number.isNaN(this.uniforms['clipBoxes'].value[i])) {
        this.uniforms['clipBoxes'].value[i] = Infinity;
      }
    }
  }

  setClipPolygons(clipPolygons: PolygonClipVolume[], _maxPolygonVertices?: number): void {
    if (!clipPolygons) {
      return;
    }

    this.clipPolygons = clipPolygons;

    const doUpdate = this.clipPolygons.length !== clipPolygons.length;

    if (doUpdate) {
      this.updateShaderSource();
    }
  }

  /** Widened to allow the custom event types dispatched throughout this class. */
  private emitEvent(event: { type: string; [key: string]: unknown }): void {
    super.dispatchEvent(event as unknown as { type: 'dispose' });
  }

  get useOrthographicCamera(): boolean {
    return this.uniforms['useOrthographicCamera'].value;
  }

  set useOrthographicCamera(value: boolean) {
    if (this.uniforms['useOrthographicCamera'].value !== value) {
      this.uniforms['useOrthographicCamera'].value = value;
    }
  }

  get backfaceCulling(): boolean {
    return this.uniforms['backfaceCulling'].value;
  }

  set backfaceCulling(value: boolean) {
    if (this.uniforms['backfaceCulling'].value !== value) {
      this.uniforms['backfaceCulling'].value = value;
      this.emitEvent({ type: 'backface_changed', target: this });
    }
  }

  recomputeClassification(): void {
    const classification = this.classification;
    const data = (this.classificationTexture.image as { data: Uint8Array }).data;

    const width = 256;
    const black = [1, 1, 1, 1];

    let valuesChanged = false;

    for (let i = 0; i < width; i++) {
      let color: number[];
      let visible = true;

      if (classification[i]) {
        color = classification[i].color;
        visible = classification[i].visible;
      } else if (classification[i % 32]) {
        color = classification[i % 32].color;
        visible = classification[i % 32].visible;
      } else if (classification['DEFAULT']) {
        color = classification['DEFAULT'].color;
        visible = classification['DEFAULT'].visible;
      } else {
        color = black;
      }

      const r = Math.floor(255 * color[0]);
      const g = Math.floor(255 * color[1]);
      const b = Math.floor(255 * color[2]);
      const a = visible ? Math.floor(255 * color[3]) : 0;

      if (data[4 * i + 0] !== r) {
        data[4 * i + 0] = r;
        valuesChanged = true;
      }

      if (data[4 * i + 1] !== g) {
        data[4 * i + 1] = g;
        valuesChanged = true;
      }

      if (data[4 * i + 2] !== b) {
        data[4 * i + 2] = b;
        valuesChanged = true;
      }

      if (data[4 * i + 3] !== a) {
        data[4 * i + 3] = a;
        valuesChanged = true;
      }
    }

    if (valuesChanged) {
      this.classificationTexture.needsUpdate = true;

      this.emitEvent({
        type: 'material_property_changed',
        target: this,
      });
    }
  }

  get spacing(): number {
    return this.uniforms['spacing'].value;
  }

  set spacing(value: number) {
    if (this.uniforms['spacing'].value !== value) {
      this.uniforms['spacing'].value = value;
    }
  }

  get useClipBox(): boolean {
    return this._useClipBox;
  }

  set useClipBox(value: boolean) {
    if (this._useClipBox !== value) {
      this._useClipBox = value;
      this.updateShaderSource();
    }
  }

  get clipTask(): number {
    return this.uniforms['clipTask'].value;
  }

  set clipTask(mode: number) {
    this.uniforms['clipTask'].value = mode;
  }

  get elevationGradientRepat(): number {
    return this.uniforms['elevationGradientRepat'].value;
  }

  set elevationGradientRepat(mode: number) {
    this.uniforms['elevationGradientRepat'].value = mode;
  }

  get clipMethod(): number {
    return this.uniforms['clipMethod'].value;
  }

  set clipMethod(mode: number) {
    this.uniforms['clipMethod'].value = mode;
  }

  get weighted(): boolean {
    return this._weighted;
  }

  set weighted(value: boolean) {
    if (this._weighted !== value) {
      this._weighted = value;
      this.updateShaderSource();
    }
  }

  get fov(): number {
    return this.uniforms['fov'].value;
  }

  set fov(value: number) {
    if (this.uniforms['fov'].value !== value) {
      this.uniforms['fov'].value = value;
    }
  }

  get screenWidth(): number {
    return this.uniforms['screenWidth'].value;
  }

  set screenWidth(value: number) {
    if (this.uniforms['screenWidth'].value !== value) {
      this.uniforms['screenWidth'].value = value;
    }
  }

  get screenHeight(): number {
    return this.uniforms['screenHeight'].value;
  }

  set screenHeight(value: number) {
    if (this.uniforms['screenHeight'].value !== value) {
      this.uniforms['screenHeight'].value = value;
    }
  }

  get near(): number {
    return this.uniforms['near'].value;
  }

  set near(value: number) {
    if (this.uniforms['near'].value !== value) {
      this.uniforms['near'].value = value;
    }
  }

  get far(): number {
    return this.uniforms['far'].value;
  }

  set far(value: number) {
    if (this.uniforms['far'].value !== value) {
      this.uniforms['far'].value = value;
    }
  }

  get opacity(): number {
    return this.uniforms['uOpacity'].value;
  }

  set opacity(value: number) {
    if (this.uniforms && this.uniforms['uOpacity']) {
      if (this.uniforms['uOpacity'].value !== value) {
        this.uniforms['uOpacity'].value = value;
        this.updateShaderSource();
        this.emitEvent({
          type: 'opacity_changed',
          target: this,
        });
        this.emitEvent({
          type: 'material_property_changed',
          target: this,
        });
      }
    }
  }

  get activeAttributeName(): string | null {
    return this._activeAttributeName;
  }

  set activeAttributeName(value: string | null) {
    if (this._activeAttributeName !== value) {
      this._activeAttributeName = value;

      this.updateShaderSource();
      this.emitEvent({
        type: 'active_attribute_changed',
        target: this,
      });

      this.emitEvent({
        type: 'material_property_changed',
        target: this,
      });
    }
  }

  get pointSizeType(): number {
    return this._pointSizeType;
  }

  set pointSizeType(value: number) {
    if (this._pointSizeType !== value) {
      this._pointSizeType = value;
      this.updateShaderSource();
      this.emitEvent({
        type: 'point_size_type_changed',
        target: this,
      });
      this.emitEvent({
        type: 'material_property_changed',
        target: this,
      });
    }
  }

  get useEDL(): boolean {
    return this._useEDL;
  }

  set useEDL(value: boolean) {
    if (this._useEDL !== value) {
      this._useEDL = value;
      this.updateShaderSource();
    }
  }

  get color(): THREE.Color {
    return this.uniforms['uColor'].value;
  }

  set color(value: THREE.Color) {
    if (!this.uniforms['uColor'].value.equals(value)) {
      this.uniforms['uColor'].value.copy(value);

      this.emitEvent({
        type: 'color_changed',
        target: this,
      });
      this.emitEvent({
        type: 'material_property_changed',
        target: this,
      });
    }
  }

  get shape(): number {
    return this._shape;
  }

  set shape(value: number) {
    if (this._shape !== value) {
      this._shape = value;
      this.updateShaderSource();
      this.emitEvent({ type: 'point_shape_changed', target: this });
      this.emitEvent({
        type: 'material_property_changed',
        target: this,
      });
    }
  }

  get treeType(): number {
    return this._treeType;
  }

  set treeType(value: number) {
    if (this._treeType !== value) {
      this._treeType = value;
      this.updateShaderSource();
    }
  }

  get bbSize(): number[] {
    return this.uniforms['bbSize'].value;
  }

  set bbSize(value: number[]) {
    this.uniforms['bbSize'].value = value;
  }

  get size(): number {
    return this.uniforms['size'].value;
  }

  set size(value: number) {
    if (this.uniforms['size'].value !== value) {
      this.uniforms['size'].value = value;

      this.emitEvent({
        type: 'point_size_changed',
        target: this,
      });
      this.emitEvent({
        type: 'material_property_changed',
        target: this,
      });
    }
  }

  get minSize(): number {
    return this.uniforms['minSize'].value;
  }

  set minSize(value: number) {
    if (this.uniforms['minSize'].value !== value) {
      this.uniforms['minSize'].value = value;

      this.emitEvent({
        type: 'point_size_changed',
        target: this,
      });
      this.emitEvent({
        type: 'material_property_changed',
        target: this,
      });
    }
  }

  get elevationRange(): number[] {
    return this.uniforms['elevationRange'].value;
  }

  set elevationRange(value: number[]) {
    const changed =
      this.uniforms['elevationRange'].value[0] !== value[0] ||
      this.uniforms['elevationRange'].value[1] !== value[1];

    if (changed) {
      this.uniforms['elevationRange'].value = value;

      this._defaultElevationRangeChanged = true;

      this.emitEvent({
        type: 'material_property_changed',
        target: this,
      });
    }
  }

  get heightMin(): number {
    return this.uniforms['elevationRange'].value[0];
  }

  set heightMin(value: number) {
    this.elevationRange = [value, this.elevationRange[1]];
  }

  get heightMax(): number {
    return this.uniforms['elevationRange'].value[1];
  }

  set heightMax(value: number) {
    this.elevationRange = [this.elevationRange[0], value];
  }

  get transition(): number {
    return this.uniforms['transition'].value;
  }

  set transition(value: number) {
    this.uniforms['transition'].value = value;
  }

  get intensityRange(): number[] {
    return this.uniforms['intensityRange'].value;
  }

  set intensityRange(value: number[]) {
    if (!(value instanceof Array && value.length === 2)) {
      return;
    }

    if (
      value[0] === this.uniforms['intensityRange'].value[0] &&
      value[1] === this.uniforms['intensityRange'].value[1]
    ) {
      return;
    }

    this.uniforms['intensityRange'].value = value;

    this._defaultIntensityRangeChanged = true;

    this.emitEvent({
      type: 'material_property_changed',
      target: this,
    });
  }

  get intensityGamma(): number {
    return this.uniforms['intensity_gbc'].value[0];
  }

  set intensityGamma(value: number) {
    if (this.uniforms['intensity_gbc'].value[0] !== value) {
      this.uniforms['intensity_gbc'].value[0] = value;
      this.emitEvent({
        type: 'material_property_changed',
        target: this,
      });
    }
  }

  get intensityContrast(): number {
    return this.uniforms['intensity_gbc'].value[2];
  }

  set intensityContrast(value: number) {
    if (this.uniforms['intensity_gbc'].value[2] !== value) {
      this.uniforms['intensity_gbc'].value[2] = value;
      this.emitEvent({
        type: 'material_property_changed',
        target: this,
      });
    }
  }

  get intensityBrightness(): number {
    return this.uniforms['intensity_gbc'].value[1];
  }

  set intensityBrightness(value: number) {
    if (this.uniforms['intensity_gbc'].value[1] !== value) {
      this.uniforms['intensity_gbc'].value[1] = value;
      this.emitEvent({
        type: 'material_property_changed',
        target: this,
      });
    }
  }

  get rgbGamma(): number {
    return this.uniforms['uRGB_gbc'].value[0];
  }

  set rgbGamma(value: number) {
    if (this.uniforms['uRGB_gbc'].value[0] !== value) {
      this.uniforms['uRGB_gbc'].value[0] = value;
      this.emitEvent({
        type: 'material_property_changed',
        target: this,
      });
    }
  }

  get rgbContrast(): number {
    return this.uniforms['uRGB_gbc'].value[2];
  }

  set rgbContrast(value: number) {
    if (this.uniforms['uRGB_gbc'].value[2] !== value) {
      this.uniforms['uRGB_gbc'].value[2] = value;
      this.emitEvent({
        type: 'material_property_changed',
        target: this,
      });
    }
  }

  get rgbBrightness(): number {
    return this.uniforms['uRGB_gbc'].value[1];
  }

  set rgbBrightness(value: number) {
    if (this.uniforms['uRGB_gbc'].value[1] !== value) {
      this.uniforms['uRGB_gbc'].value[1] = value;
      this.emitEvent({
        type: 'material_property_changed',
        target: this,
      });
    }
  }

  get extraGamma(): number {
    return this.uniforms['uExtraGammaBrightContr'].value[0];
  }

  set extraGamma(value: number) {
    if (this.uniforms['uExtraGammaBrightContr'].value[0] !== value) {
      this.uniforms['uExtraGammaBrightContr'].value[0] = value;
      this.emitEvent({
        type: 'material_property_changed',
        target: this,
      });
    }
  }

  get extraBrightness(): number {
    return this.uniforms['uExtraGammaBrightContr'].value[1];
  }

  set extraBrightness(value: number) {
    if (this.uniforms['uExtraGammaBrightContr'].value[1] !== value) {
      this.uniforms['uExtraGammaBrightContr'].value[1] = value;
      this.emitEvent({
        type: 'material_property_changed',
        target: this,
      });
    }
  }

  get extraContrast(): number {
    return this.uniforms['uExtraGammaBrightContr'].value[2];
  }

  set extraContrast(value: number) {
    if (this.uniforms['uExtraGammaBrightContr'].value[2] !== value) {
      this.uniforms['uExtraGammaBrightContr'].value[2] = value;
      this.emitEvent({
        type: 'material_property_changed',
        target: this,
      });
    }
  }

  getRange(attributeName: string): [number, number] | null | undefined {
    return this.ranges.get(attributeName);
  }

  setRange(attributeName: string, newRange: [number, number] | null): void {
    let rangeChanged = false;

    const oldRange = this.ranges.get(attributeName);

    if (oldRange != null && newRange != null) {
      rangeChanged = oldRange[0] !== newRange[0] || oldRange[1] !== newRange[1];
    } else {
      rangeChanged = true;
    }

    this.ranges.set(attributeName, newRange);

    if (rangeChanged) {
      this.emitEvent({
        type: 'material_property_changed',
        target: this,
      });
    }
  }

  get extraRange(): number[] {
    return this.uniforms['uExtraRange'].value;
  }

  set extraRange(value: number[]) {
    if (!(value instanceof Array && value.length === 2)) {
      return;
    }

    if (
      value[0] === this.uniforms['uExtraRange'].value[0] &&
      value[1] === this.uniforms['uExtraRange'].value[1]
    ) {
      return;
    }

    this.uniforms['uExtraRange'].value = value;

    this._defaultExtraRangeChanged = true;

    this.emitEvent({
      type: 'material_property_changed',
      target: this,
    });
  }

  get weightRGB(): number {
    return this.uniforms['wRGB'].value;
  }

  set weightRGB(value: number) {
    if (this.uniforms['wRGB'].value !== value) {
      this.uniforms['wRGB'].value = value;
      this.emitEvent({
        type: 'material_property_changed',
        target: this,
      });
    }
  }

  get weightIntensity(): number {
    return this.uniforms['wIntensity'].value;
  }

  set weightIntensity(value: number) {
    if (this.uniforms['wIntensity'].value !== value) {
      this.uniforms['wIntensity'].value = value;
      this.emitEvent({
        type: 'material_property_changed',
        target: this,
      });
    }
  }

  get weightElevation(): number {
    return this.uniforms['wElevation'].value;
  }

  set weightElevation(value: number) {
    if (this.uniforms['wElevation'].value !== value) {
      this.uniforms['wElevation'].value = value;
      this.emitEvent({
        type: 'material_property_changed',
        target: this,
      });
    }
  }

  get weightClassification(): number {
    return this.uniforms['wClassification'].value;
  }

  set weightClassification(value: number) {
    if (this.uniforms['wClassification'].value !== value) {
      this.uniforms['wClassification'].value = value;
      this.emitEvent({
        type: 'material_property_changed',
        target: this,
      });
    }
  }

  get weightReturnNumber(): number {
    return this.uniforms['wReturnNumber'].value;
  }

  set weightReturnNumber(value: number) {
    if (this.uniforms['wReturnNumber'].value !== value) {
      this.uniforms['wReturnNumber'].value = value;
      this.emitEvent({
        type: 'material_property_changed',
        target: this,
      });
    }
  }

  get weightSourceID(): number {
    return this.uniforms['wSourceID'].value;
  }

  set weightSourceID(value: number) {
    if (this.uniforms['wSourceID'].value !== value) {
      this.uniforms['wSourceID'].value = value;
      this.emitEvent({
        type: 'material_property_changed',
        target: this,
      });
    }
  }

  static generateGradientTexture(gradient: Gradient): THREE.CanvasTexture {
    const size = 64;

    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;

    const context = canvas.getContext('2d')!;

    context.rect(0, 0, size, size);
    const ctxGradient = context.createLinearGradient(0, 0, size, size);

    for (let i = 0; i < gradient.length; i++) {
      const step = gradient[i];
      ctxGradient.addColorStop(step[0], '#' + step[1].getHexString());
    }

    context.fillStyle = ctxGradient;
    context.fill();

    const texture = new THREE.CanvasTexture(canvas);
    texture.needsUpdate = true;

    texture.minFilter = THREE.LinearFilter;
    (texture as unknown as Record<string, unknown>)['wrap'] = THREE.RepeatWrapping;
    (texture as unknown as Record<string, unknown>)['repeat'] = 2;

    return texture;
  }

  disableEvents(): void {
    if (this._hiddenListeners === undefined) {
      this._hiddenListeners = (this as unknown as { _listeners: EventListenerMap })._listeners;
      (this as unknown as { _listeners: EventListenerMap })._listeners = {};
    }
  }

  enableEvents(): void {
    (this as unknown as { _listeners: EventListenerMap })._listeners = this._hiddenListeners!;
    this._hiddenListeners = undefined;
  }
}
